import { differenceInHours, differenceInMinutes, format, parseISO } from "date-fns";

import { NotifyType } from "@/api/notification/getNotify";
import { TypoGraph } from "@/components/ui/primitives";
import { FloorIcon, PencilIcon, PolygonIcon, SiteIcon,ThreeDIcon } from "@/components/ui/primitives/icons";

import {
  NOTIFICATION_TYPE_CONFIG,
  NotificationType,
} from "./notification.type";

interface NotiCardProps {
  notification: NotifyType;
  onClick?: (notification: NotifyType) => void;
}

export const NotiCard = ({ notification, onClick }: NotiCardProps) => {
  const parseTitle = (rawTitle: string, rawParams: string) => {
    const params = JSON.parse(rawParams);
    const regex = /{(\w+)}/g;
    let match;
    let lastIndex = 0;
    const result = [];

    while ((match = regex.exec(rawTitle)) !== null) {
      const key = match[1];
      const placeholderStart = match.index;
      const placeholderEnd = regex.lastIndex;

      if (placeholderStart > lastIndex) {
        result.push({
          text: rawTitle.slice(lastIndex, placeholderStart),
          bold: false,
        });
      }

      const param = params[key];
      if (param) {
        result.push({
          text: param.text,
          bold: param.bold,
        });
      }

      lastIndex = placeholderEnd;
    }

    if (lastIndex < rawTitle.length) {
      result.push({
        text: rawTitle.slice(lastIndex),
        bold: false,
      });
    }

    return result.map((item, index) => {
      const key = `${item.text}-${index}`;
      if (item.bold) {
        return (
          <TypoGraph as="span" variant="label04" className="text-gray-900" key={key}>
            {item.text}
          </TypoGraph>
        );
      } else {
        return (
          <TypoGraph as="span" variant="label04" className="text-gray-800" key={key}>
            {item.text}
          </TypoGraph>
        );
      }
    });
  };

  const renderNotiIcon = (notiType: NotificationType) => {
    const config = NOTIFICATION_TYPE_CONFIG[notiType];
    switch (config?.icon) {
      case "SiteIcon":
        return <SiteIcon className="text-primary-700" />;
      case "FloorIcon":
        return <FloorIcon className="text-primary-700" />;
      case "ThreeDIcon":
        return <ThreeDIcon className="text-primary-700" />;
      case "PolygonIcon":
        return <PolygonIcon className="text-primary-700" />;
      case "PencilIcon":
      default:
        return <PencilIcon className="text-primary-700" />;
    }
  };

  const getNotificationTitle = (notiType: NotificationType) => {
    return NOTIFICATION_TYPE_CONFIG[notiType]?.title || "알림";
  };

  const formatTime = (createdAt?: string) => {
    if (!createdAt) return null;
    const now = new Date();
    const created = parseISO(createdAt);
    const diffInMinutes = differenceInMinutes(now, created);
    const diffInHours = differenceInHours(now, created);

    if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
    if (diffInHours < 24) return `${diffInHours}시간 전`;

    return format(created, "yyyy-MM-dd");
  };

  const handleClick = () => {
    if (onClick) {
      onClick(notification);
    }
  };

  return (
    <div
      className="flex items-center justify-center w-[393px] min-h-[116px] shadow-[0_1px_0_0_var(--color-gray-gray300)] cursor-pointer hover:bg-gray-200"
      onClick={handleClick}
    >
      <div className="flex items-center w-[353px] h-[76px] gap-3 flex-col">
        <div className="flex w-full h-6 justify-between items-center">
          <div className="flex justify-center items-center text-center">
            <div className="flex w-6 h-6 rounded-4 bg-primary-200 justify-center items-center">
              {renderNotiIcon(notification.notificationType)}
            </div>
            <TypoGraph
              variant="label05"
              className="text-gray-900 ml-2.5 font-semibold"
            >
              {getNotificationTitle(notification.notificationType)}
            </TypoGraph>
            <span className="w-0.5 h-0.5 ml-2 rounded-full bg-gray-600" />
            <TypoGraph
              variant="label10"
              className="text-gray-700 ml-1.5 font-normal"
            >
              {formatTime(notification.createdAt)}
            </TypoGraph>
          </div>
          {!notification.isRead && (
            <div className="w-2 h-2 rounded-full bg-danger-400 shrink-0" />
          )}
        </div>

        <div className="w-full h-10">
          <TypoGraph as="div" variant="body01">
            {parseTitle(notification.title, notification.params)}
          </TypoGraph>
        </div>
      </div>
    </div>
  );
};
